import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { TableComponent } from '../../components/table/table.component';
import { RouterModule, Router } from '@angular/router';
import { DashboardStatsComponent } from './components/dashboard-stats/dashboard-stats.component';
import { UpcomingAppointmentsComponent } from './components/upcoming-appointments/upcoming-appointments.component';
import { RecentAppointmentsComponent } from './components/recent-appointments/recent-appointments.component';
import { PaymentHistoryComponent } from './components/payment-history/payment-history.component';
import { ReportsUploadedComponent } from './components/reports-uploaded/reports-uploaded.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { CalendarModule } from 'primeng/calendar';
import { FormsModule } from '@angular/forms';
import { Drawer } from 'primeng/drawer';
import { Subject, takeUntil } from 'rxjs';
import { DashboardSidebarService } from './services/sidebar.service';
import { BmiCardComponent } from './components/bmi-card/bmi-card.component';
import { AssessmentScoreCardComponent } from './components/assessment-score-card/assessment-score-card.component';
import { HydrationCardComponent } from './components/hydration-card/hydration-card.component';
import { ScoreCardSmallComponent } from './components/score-card-small/score-card-small.component';
import { BmiCardSmallComponent } from './components/bmi-card-small/bmi-card-small.component';
import { DashboardService } from './services/dashboard.service';
import { AuthService } from '../../core/auth/services/auth.service';
import { AssessmentEligibility } from './components/dashboard-const/dashboard-model';
import { AssessmentGuidComponent } from './components/assessment-guid/assessment-guid.component';
import { ActivityTrackingComponent } from '../activity-tracking/components/activity-tracking/activity-tracking.component';

interface apiData {
  doctorName: string;
  title: string;
  date: string;
  time: string;
  status: string;
}
@Component({
  selector: 'patient-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    CardModule,
    TagModule,
    ButtonModule,
    TableComponent,
    DashboardStatsComponent,
    UpcomingAppointmentsComponent,
    RecentAppointmentsComponent,
    PaymentHistoryComponent,
    ReportsUploadedComponent,
    ModalComponent,
    CalendarModule,
    FormsModule,
    Drawer,
    BmiCardComponent,
    AssessmentScoreCardComponent,
    HydrationCardComponent,
    ScoreCardSmallComponent,
    BmiCardSmallComponent,
    AssessmentGuidComponent,
    ActivityTrackingComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  // Could later be switched to API-backed observable
  data = {
    upcomingCount: 2,
    completedCount: 1,
    pendingAmount: 1500,
  };
  eligibilityData!: AssessmentEligibility | null;
  patientId!: string;
  visible = false;
  sidebarData: apiData | null = null;
  dashboardStats: any = null;
  private destroy$ = new Subject<void>();
  private DashboardSidebarService = inject(DashboardSidebarService);
  private dashboardService = inject(DashboardService);
  private authService = inject(AuthService);

  constructor(private router: Router) {}

  ngOnInit(): void {
    this.date = new Date();
    this.DashboardSidebarService.sidebarVisible$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => (this.visible = v));
    this.DashboardSidebarService.currentSidebarData$
      .pipe(takeUntil(this.destroy$))
      .subscribe((d) => (this.sidebarData = d));

    this.authService.getUserProfile().subscribe((res) => {
      if (res?.PatientId) {
        this.patientId = res.PatientId;

        this.loadDashboardStats(this.patientId);
        this.getPreAssementEleigibility(this.patientId);

      }
    });
  }

  getPreAssementEleigibility(id: string) {
    this.dashboardService.getPreAssementEleigibility(id).subscribe({
      next: (res: any) => {
        if (res.status === 0 || res.status === 200) {
          this.eligibilityData = res.data;
        }
      },
      error: (err: any) => {
        console.error('Error checking pre-assessment eligibility', err);
      },
    });
  }

  loadDashboardStats(id: string) {
    this.dashboardService
      .getDashboardStats(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (res.status === 0 || res.status === 200) {
            this.dashboardStats = res.data;
            // Parse nested score data if it's a string
            if (typeof this.dashboardStats.assessment_data?.latest_score === 'string') {
              try {
                const scoreData = JSON.parse(this.dashboardStats.assessment_data.latest_score);
                this.dashboardStats.assessment_data.total_score =
                  scoreData.actual_pre_assessment_score?.total_score || 0;
              } catch (e) {
                console.error('Error parsing latest_score', e);
                this.dashboardStats.assessment_data.total_score = 0;
              }
            } else if (
              this.dashboardStats.assessment_data?.latest_score?.actual_pre_assessment_score
            ) {
              this.dashboardStats.assessment_data.total_score =
                this.dashboardStats.assessment_data.latest_score.actual_pre_assessment_score.total_score;
            }
          }
        },
        error: (err: any) => console.error('Error fetching dashboard stats', err),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHide() {
    this.DashboardSidebarService.hideSidebar();
  }

  onUpcomingAppointmentClick(item: any) {
    this.router.navigate(['/appointment-details'], {
      state: { details: { ...item, type: 'upcoming' } },
    });
  }

  onRecentAppointmentClick(item: any) {
    this.router.navigate(['/appointment-details'], {
      state: { details: { ...item, type: 'recent' } },
    });
  }

  reports = [
    { name: 'Blood Test Report', uploadedDate: '12 Oct 2024', size: '320 KB' },
    { name: 'MRI Scan', uploadedDate: '08 Sep 2024', size: '1.2 MB' },
  ];

  date: Date | undefined;

  onReschedule() {}
  onCancel() {}

  formatKey(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim();
  }

  formatValue(value: any): any {
    if (!value && value !== 0) return '-';

    // ISO date check
    if (typeof value === 'string' && value.includes('T')) {
      return new Date(value).toLocaleString();
    }

    return value;
  }
}
